import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  homeBlocksApi,
  type HomeBlockInput,
  type HomeBlockReorderItem,
} from "@/lib/api";
import { queryKeys } from "./keys";

export function useHomeBlocks() {
  return useQuery({
    queryKey: queryKeys.homeBlocks.list,
    queryFn: homeBlocksApi.listHomeBlocks,
  });
}

function useInvalidateHomeBlocks() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.homeBlocks.list });
}

export function useCreateHomeBlock() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: (payload: HomeBlockInput) => homeBlocksApi.createHomeBlock(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useUpdateHomeBlock() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: HomeBlockInput }) =>
      homeBlocksApi.updateHomeBlock(id, payload),
    onSuccess: () => void invalidate(),
  });
}

export function useDeleteHomeBlock() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: (blockId: string) => homeBlocksApi.deleteHomeBlock(blockId),
    onSuccess: () => void invalidate(),
  });
}

export function useBulkDeleteHomeBlocks() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: (ids: string[]) => homeBlocksApi.bulkDeleteHomeBlocks(ids),
    onSuccess: () => void invalidate(),
  });
}

export function useReorderHomeBlocks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: HomeBlockReorderItem[]) => homeBlocksApi.reorderHomeBlocks(items),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.homeBlocks.list, data);
    },
  });
}

export function usePublishHomeBlock() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: (blockId: string) => homeBlocksApi.publishHomeBlock(blockId),
    onSuccess: () => void invalidate(),
  });
}

export function useUnpublishHomeBlock() {
  const invalidate = useInvalidateHomeBlocks();
  return useMutation({
    mutationFn: (blockId: string) => homeBlocksApi.unpublishHomeBlock(blockId),
    onSuccess: () => void invalidate(),
  });
}
